import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Stack,
  Button,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LeadFormDialog from "./LeadFormDialog";
import ContactoDialog from "./ContactoDialog";
import EnviarWADialog from "./EnviarWADialog";
import LeadRow from "./LeadRow";
import { etapaLabel, canalLabel } from "./pipelineUtils";
import ImportarLeadsMasivo from "./ImportarLeadsMasivo";
import SectionHeader from "./ui/SectionHeader";
import OportunidadFormDialog from "./expansion/OportunidadFormDialog";
import OportunidadSeguimientoDialog from "./expansion/OportunidadSeguimientoDialog";

export default function LeadsList({ store, onNuevaVenta }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mostrarArchivados, setMostrarArchivados] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [filasPorPagina, setFilasPorPagina] = useState(25);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEtapa, setFiltroEtapa] = useState("");
  const [filtroCanal, setFiltroCanal] = useState("");
  const [filtroZona, setFiltroZona] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [contactoLeadId, setContactoLeadId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [waLead, setWaLead] = useState(null);
  const [oppFormOpen, setOppFormOpen] = useState(false);
  const [oppEdit, setOppEdit] = useState(null);
  const [oppLeadId, setOppLeadId] = useState("");
  const [seguimientoOpp, setSeguimientoOpp] = useState(null);

  const expansionHandlers = {
    onNuevaOportunidad: (id) => { setOppLeadId(id); setOppEdit(null); setOppFormOpen(true); },
    onEditOportunidad: (o) => { setOppEdit(o); setOppLeadId(o.leadId); setOppFormOpen(true); },
    onSeguimientoOportunidad: setSeguimientoOpp,
  };

  const leads = useMemo(() => (mostrarArchivados && store.isOwner ? store.leadsArchivados : store.leads)
    .filter((l) => {
      const q = busqueda.toLowerCase();
      if (q && !l.nombre.toLowerCase().includes(q) && !l.telefono?.includes(q) && !l.cursoInteres?.toLowerCase().includes(q) && !(l.zona || "").toLowerCase().includes(q))
        return false;
      if (filtroEtapa && l.etapa !== filtroEtapa) return false;
      if (filtroCanal && l.canal !== filtroCanal) return false;
      if (filtroZona && l.zona !== filtroZona) return false;
      return true;
    })
    .sort((a, b) => (b.creadoEn || "").localeCompare(a.creadoEn || "")), [mostrarArchivados, store.isOwner, store.leadsArchivados, store.leads, busqueda, filtroEtapa, filtroCanal, filtroZona]);
  const paginaActual = Math.min(pagina, Math.max(0, Math.ceil(leads.length / filasPorPagina) - 1));
  const leadsVisibles = leads.slice(paginaActual * filasPorPagina, (paginaActual + 1) * filasPorPagina);

  const rowStore = mostrarArchivados ? { ...store, ...store.historialArchivado } : store;
  const contactosDelLead = (leadId) =>
    (expandedId === leadId ? rowStore.contactosHistorial : [])
      .filter((c) => c.leadId === leadId)
      .sort((a, b) => b.fecha.localeCompare(a.fecha));

  return (
    <Box>
      <SectionHeader
        title={`${mostrarArchivados ? "Archivados" : "Leads"} (${leads.length})`}
        description="Busca, filtra, contacta e importa tus prospectos."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditData(null); setOpenForm(true); }} sx={{ flexShrink: 0 }}>
            Nuevo lead
          </Button>
        }
        sx={{ mb: 2 }}
      />

      {store.isOwner && <Button sx={{ mb: 2 }} variant="outlined" onClick={() => { setMostrarArchivados(!mostrarArchivados); setPagina(0); setExpandedId(null); }}>
        {mostrarArchivados ? "Volver a clientes" : `Archivados (${store.leadsArchivados.length})`}
      </Button>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={2}>
        <TextField label="Buscar" placeholder="Nombre, teléfono, interés o zona" size="small" value={busqueda} onChange={(e) => { setBusqueda(e.target.value); setPagina(0); setExpandedId(null); }} sx={{ minWidth: 200 }} />
        <TextField label="Etapa" size="small" select value={filtroEtapa} onChange={(e) => { setFiltroEtapa(e.target.value); setPagina(0); setExpandedId(null); }} sx={{ minWidth: 140 }}>
          <MenuItem value="">Todas</MenuItem>
          {store.config.etapasCatalogo.map((e) => (<MenuItem key={e.id} value={e.id}>{e.nombre}{e.activa ? "" : " (oculta)"}</MenuItem>))}
        </TextField>
        <TextField label="Canal" size="small" select value={filtroCanal} onChange={(e) => { setFiltroCanal(e.target.value); setPagina(0); setExpandedId(null); }} sx={{ minWidth: 140 }}>
          <MenuItem value="">Todos</MenuItem>
          {store.config.canales.map((c) => (<MenuItem key={c} value={c}>{canalLabel(c)}</MenuItem>))}
        </TextField>
        {store.config.zonas?.length > 0 && (
          <TextField label="Zona" size="small" select value={filtroZona} onChange={(e) => { setFiltroZona(e.target.value); setPagina(0); setExpandedId(null); }} sx={{ minWidth: 140 }}>
            <MenuItem value="">Todas</MenuItem>
            {store.config.zonas.map((z) => (<MenuItem key={z} value={z}>{z}</MenuItem>))}
          </TextField>
        )}
      </Stack>

      <ImportarLeadsMasivo store={store} />

      {isMobile ? (
        <Stack spacing={1.5}>
          {leadsVisibles.map((lead) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              etapaNombre={etapaLabel(store.config, lead.etapa)}
              isMobile
              contactos={contactosDelLead(lead.id)}
              config={store.config}
              historialEtapas={rowStore.historialEtapas}
              historialEdicionesLead={rowStore.historialEdicionesLead}
              historialAsignaciones={rowStore.historialAsignaciones}
              isExpanded={expandedId === lead.id}
              onToggle={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
              onWA={() => setWaLead(lead)}
              onContacto={() => setContactoLeadId(lead.id)}
              onEdit={() => { setEditData(lead); setOpenForm(true); }}
              onDelete={() => store.eliminarLead(lead.id)}
              diasEntreSeguimiento={store.config.diasEntreSeguimiento || 0}
              store={rowStore}
              {...expansionHandlers}
            />
          ))}
          {leads.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No hay leads registrados
            </Typography>
          )}
        </Stack>
      ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Producto / Servicio</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Canal</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Etapa</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Último contacto</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leadsVisibles.map((lead) => (
              <LeadRow
                key={lead.id}
                lead={lead}
                etapaNombre={etapaLabel(store.config, lead.etapa)}
                contactos={contactosDelLead(lead.id)}
                config={store.config}
                historialEtapas={rowStore.historialEtapas}
                historialEdicionesLead={rowStore.historialEdicionesLead}
                historialAsignaciones={rowStore.historialAsignaciones}
                isExpanded={expandedId === lead.id}
                onToggle={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                onWA={() => setWaLead(lead)}
                onContacto={() => setContactoLeadId(lead.id)}
                onEdit={() => { setEditData(lead); setOpenForm(true); }}
                onDelete={() => store.eliminarLead(lead.id)}
                diasEntreSeguimiento={store.config.diasEntreSeguimiento || 0}
                store={rowStore}
                {...expansionHandlers}
              />
            ))}
            {leads.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No hay leads registrados</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <TablePagination component="div" count={leads.length} page={paginaActual} rowsPerPage={filasPorPagina} rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Clientes por página" labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`} onPageChange={(_, page) => { setPagina(page); setExpandedId(null); }} onRowsPerPageChange={e => { setFilasPorPagina(Number(e.target.value)); setPagina(0); setExpandedId(null); }} />
      <LeadFormDialog open={openForm} onClose={() => { setOpenForm(false); setEditData(null); }} store={store} editData={editData} onNuevaVenta={onNuevaVenta} />
      <ContactoDialog open={Boolean(contactoLeadId)} onClose={() => setContactoLeadId(null)} store={store} leadId={contactoLeadId} />
      <EnviarWADialog open={Boolean(waLead)} onClose={() => setWaLead(null)} store={store} lead={waLead} />
      <OportunidadFormDialog
        open={oppFormOpen}
        onClose={() => { setOppFormOpen(false); setOppEdit(null); }}
        store={store}
        editData={oppEdit}
        defaultLeadId={oppLeadId}
      />
      <OportunidadSeguimientoDialog
        open={!!seguimientoOpp}
        onClose={() => setSeguimientoOpp(null)}
        store={store}
        oportunidad={seguimientoOpp}
      />
    </Box>
  );
}
